import { StateMessage } from '@/components/ui/controls';
import { Card } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import StatTile from '@/components/ui/stat-tile';
import { table } from '@/components/ui/styles';
import { MANUAL_STAGES, STAGES, sourceLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';
import { useSort, type SortColumns } from '@/utils/use-sort';

import type { ReportFunnel } from './api-types';
import HBarChart from './hbar-chart';
import ReportsHeader from './reports-nav';
import { percent } from './series';
import TableCard from './table-card';

// The funnel's steps: the platform stages. Clarifications, set by a person, and the blocked tag sit beside it.
const FUNNEL = STAGES.filter((s) => !MANUAL_STAGES.includes(s.value));

type SourceRow = ReportFunnel['sources'][number] & { label: string; rate: number };

// Per source: its name, how many reached each step, and the rate to a sale.
const SOURCE_COLUMNS: SortColumns<SourceRow> = {
  source: { value: (f) => f.label },
  ...Object.fromEntries(FUNNEL.map((s) => [s.value, { value: (f: SourceRow) => f.reached[s.value] ?? 0, desc: true }])),
  rate: { value: (f) => f.rate, desc: true },
};

/**
 * Reports, Funnel (LEAD-4): how far leads got, from lead to sale, overall and per
 * first-touch source (GET /reports/funnel-by-source). It counts where the platform has
 * each lead, whatever a person set; the leads in Clarifications or tagged Blocked are counted
 * apart. A lead "reached" a stage when it is there now or further on. On a phone the
 * tables keep the columns that matter most (UI-1).
 */
const FunnelReportPage = () => {
  const funnel = useApi<ReportFunnel>('/reports/funnel-by-source');
  const sources = funnel.data?.sources ?? [];
  const total = sources.reduce((n, f) => n + f.leads, 0);
  // Platform stages only move forward (CAS-4), so reaching one means being there now or at any later one.
  const reached = FUNNEL.map((s) => sources.reduce((n, f) => n + (f.reached[s.value] ?? 0), 0));
  const now = (i: number) => reached[i] - (reached[i + 1] ?? 0);
  const sales = reached[FUNNEL.length - 1];
  const ready = funnel.data !== null;
  const side = funnel.data?.side;

  const steps = FUNNEL.map((s, i) => ({ key: s.value, label: s.label, value: reached[i] }));
  const bySource: SourceRow[] = (funnel.data?.sources ?? []).map((f) => ({
    ...f,
    label: f.source === 'unknown' ? 'Unknown' : sourceLabel(f.source),
    rate: f.leads ? Math.round(((f.reached.live ?? 0) / f.leads) * 100) : 0,
  }));

  const sourceSort = useSort(bySource, SOURCE_COLUMNS);

  return (
    <div>
      <ReportsHeader />
      <p className="-mt-2 mb-6 text-sm text-fg-muted">
        The stages are a proposal for Matt and the Sales team to confirm (Q4). The funnel counts where the platform has each lead; Clarifications
        and the Blocked tag are set by the team and counted apart.
      </p>
      {funnel.error && !ready ? (
        <StateMessage title="The funnel could not be loaded" tone="error">
          {funnel.error}
        </StateMessage>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-6">
            <StatTile label="Leads" value={ready ? String(total) : '…'} note="Everyone at any stage" />
            <StatTile label="Started a casino" value={ready ? String(reached[1]) : '…'} note={`${percent(reached[1], total)} of leads`} />
            <StatTile label="Sales" value={ready ? String(sales) : '…'} note="A casino is live (LEAD-4)" />
            <StatTile label="Lead to sale" value={ready ? percent(sales, total) : '…'} note={`${sales} of ${total} leads`} />
            <StatTile label="Clarifications" value={side ? String(side.clarifications) : '…'} note="Asking questions; set by the team" />
            <StatTile label="Blocked" value={side ? String(side.blocked) : '…'} note="A tag the team sets on Configuring or Clarifications" />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-[3fr_2fr]">
            <Card title="From lead to sale">
              <HBarChart
                rows={steps}
                format={String}
                label="Leads that reached each stage"
                tooltip={(r) => (
                  <>
                    <span className="font-semibold text-fg-strong">{r.value}</span>
                    <span className="ml-1.5 text-fg-muted">
                      reached {r.label}, {percent(r.value, total)} of leads
                    </span>
                  </>
                )}
                after={(_, i) =>
                  i < steps.length - 1 && ready ? `↓ ${percent(reached[i + 1], reached[i])} went on to ${FUNNEL[i + 1].label.toLowerCase()}` : null
                }
              />
            </Card>
            <TableCard title="Stages">
              <thead className={table.head}>
                <tr>
                  <th scope="col" className={table.th}>Stage</th>
                  <th scope="col" className={`${table.th} hidden text-right md:table-cell`}>There now</th>
                  <th scope="col" className={`${table.th} text-right`}>Reached</th>
                  <th scope="col" className={`${table.th} text-right`}>From the step before</th>
                </tr>
              </thead>
              <tbody>
                {FUNNEL.map((s, i) => (
                  <tr key={s.value} className={table.row}>
                    <td className={table.td} title={s.hint}>
                      {s.label}
                    </td>
                    <td className={`${table.td} hidden text-right tabular-nums md:table-cell`}>{now(i)}</td>
                    <td className={`${table.td} text-right tabular-nums`}>{reached[i]}</td>
                    <td className={`${table.td} text-right tabular-nums text-fg-muted`}>{i ? percent(reached[i], reached[i - 1]) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[3fr_2fr]">
            <Card title="Lead to sale by first-touch source">
              {funnel.error && !funnel.data && <p className="text-sm text-danger">The funnel per source could not be loaded.</p>}
              <HBarChart
                rows={bySource.map((f) => ({ key: f.source, label: f.label, value: f.rate, muted: f.source === 'unknown' }))}
                format={(v) => `${v}%`}
                label="Share of each source's leads that became a sale"
                tooltip={(r) => {
                  const f = bySource.find((x) => x.source === r.key);
                  return (
                    <>
                      <span className="font-semibold text-fg-strong">{r.value}%</span>
                      <span className="ml-1.5 text-fg-muted">
                        {f?.reached.live ?? 0} of {f?.leads} {r.label} leads became a sale
                      </span>
                    </>
                  );
                }}
              />
              <p className="mt-4 text-xs text-fg-faint">All leads, by where they first came from.</p>
            </Card>
            <TableCard title="Per source">
              <thead className={table.head}>
                <tr>
                  <SortTh label="Source" column="source" sort={sourceSort.sort} onSort={sourceSort.toggle} />
                  {FUNNEL.map((s, i) => (
                    <SortTh
                      key={s.value}
                      label={
                        <>
                          {i === 0 ? <span className="md:hidden">Leads</span> : null}
                          <span className={i === 0 ? 'hidden md:inline' : undefined}>{s.label}</span>
                        </>
                      }
                      column={s.value}
                      sort={sourceSort.sort}
                      onSort={sourceSort.toggle}
                      right
                      className={i > 0 ? 'hidden md:table-cell' : undefined}
                    />
                  ))}
                  <SortTh label="To sale" column="rate" sort={sourceSort.sort} onSort={sourceSort.toggle} right />
                </tr>
              </thead>
              <tbody>
                {sourceSort.rows.map((f) => (
                  <tr key={f.source} className={table.row}>
                    <td className={`${table.td} whitespace-nowrap`}>{f.label}</td>
                    {FUNNEL.map((s, i) => (
                      <td key={s.value} className={`${table.td} text-right tabular-nums ${i > 0 ? 'hidden md:table-cell' : ''}`}>
                        {f.reached[s.value] ?? 0}
                      </td>
                    ))}
                    <td className={`${table.td} text-right tabular-nums text-fg-strong`}>{f.rate}%</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>
          </div>
        </>
      )}
    </div>
  );
};

export default FunnelReportPage;
